import React from "react";
import { motion } from "framer-motion";
import {
  Briefcase,
  Scale,
  UsersRound,
  FileText,
  GraduationCap,
  Plane,
  Languages,
  Home,
  ArrowUpRight,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const ICONS = [Briefcase, Scale, UsersRound, FileText, GraduationCap, Plane, Languages, Home];

const Services = () => {
  const { t, dir } = useLanguage();

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    e.currentTarget.style.setProperty("--mx", `${x}%`);
    e.currentTarget.style.setProperty("--my", `${y}%`);
  };

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-8 md:py-12"
      style={{
        background:
          "linear-gradient(180deg, transparent 0%, rgba(31,107,122,0.08) 12%, rgba(31,107,122,0.12) 50%, rgba(31,107,122,0.08) 88%, transparent 100%)",
      }}
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <span className="eyebrow" data-testid="services-eyebrow">
            {t.services.eyebrow}
          </span>
          <h2 className="display display-2 mt-4 text-[color:var(--kompass-ink)]">
            {t.services.title}
          </h2>
          <p className="mt-5 text-base md:text-lg leading-relaxed text-[color:var(--kompass-ink-soft)]">
            {t.services.subtitle}
          </p>
        </motion.div>

        <div
          className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
          data-testid="services-grid"
        >
          {t.services.list.map((s, i) => {
            const Icon = ICONS[i] || Briefcase;
            return (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.07 }}
                onMouseMove={handleMouseMove}
                className="svc-card group"
                data-testid={`service-card-${i}`}
              >
                <div
                  className="flex h-12 w-12 items-center justify-center rounded-xl mb-6"
                  style={{ background: "rgba(31,107,122,0.08)" }}
                >
                  <Icon
                    className="h-6 w-6"
                    strokeWidth={1.5}
                    style={{ color: "var(--kompass-primary)" }}
                  />
                </div>
                <h3 className="display text-lg font-semibold text-[color:var(--kompass-ink)] leading-snug">
                  {s.title}
                </h3>
                <p className="mt-3 text-[14.5px] leading-relaxed text-[color:var(--kompass-ink-soft)]">
                  {s.text}
                </p>
                <div className="mt-6 flex items-center gap-2 text-[color:var(--kompass-primary)] text-sm font-semibold opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300">
                  <span>—</span>
                  <ArrowUpRight
                    className={`h-4 w-4 ${dir === "rtl" ? "flip-x" : ""}`}
                  />
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
